// The client-side view of application/problem+json (packages/contracts/src/problems.ts). Every non-2xx answer becomes one
// ApiError; a request refused on the device while it is offline, or whose answer never arrived (a network failure, a body cut off),
// is a NetworkError.
import type { FieldError, Problem, ProblemCode } from "@visionx/contracts";

export class ApiError extends Error {
  readonly code: ProblemCode;
  readonly status: number;
  readonly title: string;
  readonly detail: string | undefined;
  readonly errors: FieldError[];
  readonly meta: Record<string, unknown>;
  readonly requestId: string;
  constructor(problem: Problem) {
    super(problem.detail ?? problem.title);
    this.name = "ApiError";
    this.code = problem.code;
    this.status = problem.status;
    this.title = problem.title;
    this.detail = problem.detail;
    this.errors = problem.errors ?? [];
    this.meta = problem.meta ?? {};
    this.requestId = problem.requestId;
  }
  /** The first field error for a path, for inline form messages. */
  field(path: string): FieldError | undefined { return this.errors.find((e) => e.path === path); }
  is(...codes: ProblemCode[]): boolean { return codes.includes(this.code); }
}

export class NetworkError extends Error {
  /**
   * True only for a write the client refused to send because the device was offline (D13): nothing was sent. A request handed to
   * fetch that then fails is a lost answer, `offline` false, whatever the device reports by then — it may have landed (BR-TENANCY-031).
   */
  readonly offline: boolean;
  constructor(message: string, offline: boolean) {
    super(message);
    this.name = "NetworkError";
    this.offline = offline;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;
export const isNetworkError = (e: unknown): e is NetworkError => e instanceof NetworkError;

/**
 * A write sent again under its key whose first sending was carried out, its answer not kept (`409 REQUEST_COMPLETED`;
 * BR-GOVERNANCE-016): the status it was answered with — else null. A screen that sends a write again under its key after a lost
 * answer takes it as done: it reads the record again and goes on as for that answer, never saying the write was not made
 * (BR-INVENTORY-025; the verification of round 3, 25 Oct 2026).
 */
export function carriedOut(e: unknown): { answeredStatus: number } | null {
  if (!isApiError(e) || e.code !== "REQUEST_COMPLETED") return null;
  const status = e.meta.answeredStatus;
  return { answeredStatus: typeof status === "number" ? status : 200 };
}

/** One sentence for an alert. Never echoes a stack; the request id is appended for a 500 so support can find it. */
export function describeError(e: unknown): string {
  if (isApiError(e)) {
    if (e.code === "INTERNAL_ERROR") return `Something went wrong. Request ${e.requestId}.`;
    if (e.code === "VALIDATION_FAILED" && e.errors.length > 0) return e.errors.map((f) => `${f.path}: ${f.message}`).join("; ");
    return e.detail ?? e.title;
  }
  // D13: a write made offline is refused before it is sent and nothing keeps it — only Lock and Sign out wait (BR-TENANCY-031).
  if (isNetworkError(e)) return e.offline ? "You are offline. Nothing was sent; try again once you are back online." : "The server could not be reached.";
  return e instanceof Error ? e.message : String(e);
}

const problemLike = (v: unknown): v is Problem =>
  typeof v === "object" && v !== null && typeof (v as Problem).code === "string" && typeof (v as Problem).status === "number";

/** Parses a problem body; an answer that is not problem+json still becomes an ApiError so callers see one type. */
export function problemFrom(status: number, body: unknown, requestId: string): ApiError {
  if (problemLike(body)) return new ApiError(body);
  const code: ProblemCode = status === 401 ? "AUTHENTICATION_REQUIRED" : status === 403 ? "PERMISSION_DENIED" : status === 404 ? "NOT_FOUND" : status === 429 ? "RATE_LIMITED" : "INTERNAL_ERROR";
  const p: Problem = { type: "about:blank", title: code, status, code, requestId };
  if (typeof body === "string" && body) p.detail = body.slice(0, 200);
  return new ApiError(p);
}
